/* "Is this prayer still on time?" — the current prayer's window, split into
   three phases like Prayerly: on time (green), getting late (amber), and
   ending (red). Pure, so it is unit-tested.

   Windows (ends are when the time for that prayer is over):
     Fajr     Fajr → sunrise
     Dhuhr    Dhuhr → ʿAsr
     ʿAsr     ʿAsr → Maghrib
     Maghrib  Maghrib → ʿIshāʾ
     ʿIshāʾ   ʿIshāʾ → middle of the night; after that it is red until Fajr
   Between sunrise and Dhuhr there is no fard prayer due (Ḍuḥā time).

   Green is the preferred time, amber is allowed but not preferred, red is the part of the time
   that is disliked or about to end. The schools differ in places; where they do, this takes what
   they share and the app's ʿAsr madhab setting. What each prayer's preferred time is:

     Fajr     Early. The Prophet ﷺ prayed it in ghalas, the dark mixed with the first light, and
              finished when a man could make out his neighbour (al-Bukhārī, Muslim); the Ḥanafīs
              prefer isfār, when it has brightened, but leave time to repeat it. → green for the first half
              of the window, red for the last 20 minutes so the sun does not rise in the prayer.
     Dhuhr    Early; in great heat it may be cooled (ibrād) but "not delayed until the end of its time"
              (al-Bukhārī, Muslim). → green to the middle of the window, red for the last fifth.
     ʿAsr     Preferred until the shadow is twice its length (an-Nawawī; the Ḥanafī start of ʿAsr);
              then allowed until the sun yellows; from the yellowing to sunset it is disliked
              (makrūh) — "the prayer of the hypocrite" (Muslim). → green to double shadow, amber to
              about 20 minutes before sunset, red after. A Ḥanafī (whose ʿAsr already starts at double
              shadow) has no amber: green until the yellowing.
     Maghrib  Hasten it: the time is short. Some hold it lasts only as long as wuḍūʾ, adhān,
              iqāma and the prayer take (Mālikī, later Shāfiʿī); the majority, until the red twilight
              has gone (Muslim); and "my ummah stays well as long as it does not delay Maghrib until
              the stars appear" (Abū Dāwūd, Aḥmad). → green for the first 20 minutes, amber until the
              red twilight is gone (sun ≈ 12° below the horizon), red until ʿIshāʾ.
     ʿIshāʾ   Delaying it is best, up to a third of the night, or half if it is no hardship (at-Tirmidhī,
              Muslim); it is disliked to delay it past the middle of the night. → green to a third of
              the night, amber to the middle of the night, red after (still valid until Fajr). */
import { addDays, dayKeyIn, prayerPhases as phases, timesFor, type Fard, type PhaseNight as Night, type PrayerSettings } from '@shared/prayer';

export type Level = 'ontime' | 'late' | 'ending';
export type { Fard };

export interface PrayerWindow {
  key: Fard;
  /** day the prayer belongs to (ʿIshāʾ after midnight belongs to yesterday) */
  dayKey: string;
  start: Date;
  end: Date;
  level: Level;
  remainingMs: number;
  /** past the preferred end (ʿIshāʾ after midnight) — still valid, but late */
  overdue: boolean;
  /** boundaries where amber and red begin (equal when there is no amber stretch) */
  lateAt: Date;
  endingAt: Date;
  /** one line: when this prayer is best prayed */
  hint: string;
}

/** When each prayer is best prayed, in a line (shown under the timer). */
export const PREFERRED_HINT: Record<Fard, string> = {
  fajr: 'Best early, while it is still dim — not left until sunrise is near.',
  dhuhr: 'Best early. In great heat it may be cooled, but not left until the end.',
  asr: 'Best before the sun yellows; delaying until then is disliked.',
  maghrib: 'Pray soon after sunset — its time is short.',
  isha: 'Best delayed, up to a third of the night; after midnight is late.',
};

const MIN = 60_000;
/* The phase boundaries themselves (prayerPhases) live in shared/prayer.ts, so the
   push worker's "running late" reminder uses exactly the timer's green. */

export function currentWindow(s: PrayerSettings, now = new Date()): PrayerWindow | null {
  const today = dayKeyIn(s.tz, now);
  const t = timesFor(s, today);
  const n = now.getTime();
  const make = (key: Fard, dayKey: string, start: Date, end: Date, night: Night, hardEnd?: Date): PrayerWindow => {
    const p = phases(key, s, dayKey, start.getTime(), end.getTime(), night);
    let level: Level = n < p.lateAt ? 'ontime' : n < p.endingAt ? 'late' : 'ending';
    let finish = end;
    const overdue = !!hardEnd && n >= end.getTime();
    if (overdue) { level = 'ending'; finish = hardEnd!; }
    return { key, dayKey, start, end: finish, level, overdue, remainingMs: Math.max(0, finish.getTime() - n), lateAt: new Date(p.lateAt), endingAt: new Date(p.endingAt), hint: PREFERRED_HINT[key] };
  };
  const night = (maghrib: Date, nextFajr: Date): Night => ({ maghrib: maghrib.getTime(), nextFajr: nextFajr.getTime() });

  if (n < t.fajr.getTime()) {
    // Before Fajr: still last night's ʿIshāʾ (preferred until the middle of the night, then red until Fajr).
    const y = timesFor(s, addDays(today, -1));
    return n >= y.isha.getTime() ? make('isha', y.dayKey, y.isha, y.midnight, night(y.maghrib, t.fajr), t.fajr) : null;
  }
  if (n < t.sunrise.getTime()) return make('fajr', today, t.fajr, t.sunrise, night(t.maghrib, t.fajr));
  if (n < t.dhuhr.getTime()) return null;
  if (n < t.asr.getTime()) return make('dhuhr', today, t.dhuhr, t.asr, night(t.maghrib, t.fajr));
  if (n < t.maghrib.getTime()) return make('asr', today, t.asr, t.maghrib, night(t.maghrib, t.fajr));
  if (n < t.isha.getTime()) return make('maghrib', today, t.maghrib, t.isha, night(t.maghrib, t.fajr));
  const nextFajr = timesFor(s, addDays(today, 1)).fajr;
  return make('isha', today, t.isha, t.midnight, night(t.maghrib, nextFajr), nextFajr);
}

/** What "I've prayed" logs: wherever the timer is right now — green is on time, amber or red is late. */
export const statusForLevel = (level: Level): 'ontime' | 'late' => (level === 'ontime' ? 'ontime' : 'late');

export const LEVEL_LABEL: Record<Level, string> = { ontime: 'On time', late: 'Getting late', ending: 'Time is ending' };

/** Seconds are shown once the next prayer is this close. */
export const SECONDS_WITHIN_MS = 15 * MIN;

/** Countdown to the next prayer: "2h 05m", "42 min", then "12m 05s" in the last 15 minutes. */
export function fmtUntil(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (ms <= SECONDS_WITHIN_MS) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  return fmtLeft(ms);
}

export function fmtLeft(ms: number) {
  const m = Math.ceil(ms / MIN);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}
